import React, { useState, useMemo, useCallback } from "react";
import { X, Plus, Trash2, Send, Printer, CheckCircle2, PackageCheck, FileWarning, Receipt, Lock } from "lucide-react";
import { doc, updateDoc, serverTimestamp, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { httpsCallable, getFunctions } from "firebase/functions";
import { confirm } from "../utils/confirm";
import { useToast } from "../utils/Toast";
import { logModifica } from "../utils/logModifica";
import { formatValutaItaliana, parseValutaItaliana } from "../utils/clienteHelpers";
import { toLocalDateStr } from "../utils/dateUtils";
import { dominioSito } from "../utils/fornitore";
import { useListinoMateriali, trovaInListino } from "./ArticoliNF";
import { PannelloDoppioni } from "./ControlloDoppioni";
import { differenzeOdaNf, testoDifferenze } from "../utils/acquisti";

// ─────────────────────────────────────────────────────────────────────────
// STATI ODA — percorso completo del ciclo di acquisto, non solo "inviato o no".
// L'ordine dell'array è anche l'ordine di avanzamento naturale mostrato nei
// pulsanti d'azione; "Annullato" è raggiungibile da qualunque stato non ancora
// chiuso, quindi vive a parte.
// ─────────────────────────────────────────────────────────────────────────
export const STATI_ODA = [
  "Bozza", "Da approvare", "Approvato", "Inviato", "Confermato",
  "Parzialmente ricevuto", "Ricevuto", "Fattura verificata", "Chiuso",
];
export const STATO_ODA_COLORI = {
  "Bozza":                 { bg:"#f3f4f6", text:"#6b7280" },
  "Da approvare":          { bg:"#fef3c7", text:"#b45309" },
  "Approvato":             { bg:"#e0e7ff", text:"#4338ca" },
  "Inviato":               { bg:"#dbeafe", text:"#2563eb" },
  "Confermato":            { bg:"#cffafe", text:"#0e7490" },
  "Parzialmente ricevuto": { bg:"#fed7aa", text:"#c2410c" },
  "Ricevuto":              { bg:"#d1fae5", text:"#059669" },
  "Fattura verificata":    { bg:"#ddd6fe", text:"#6d28d9" },
  "Chiuso":                { bg:"#e5e7eb", text:"#374151" },
  "Annullato":             { bg:"#fee2e2", text:"#dc2626" },
};

// Una riga vuota di partenza — id locale (non l'id del documento) solo per
// tenere ordine nell'editor finché non si salva.
let rigaSeq = 0;
const nuovaRiga = () => ({ id: `r${Date.now()}_${rigaSeq++}`, descrizione:"", codice:"", quantita:"", unitaMisura:"", prezzoUnitario:"", sconto:"", iva:"22" });

export function calcolaTotaleRiga(r) {
  const q = parseFloat(String(r.quantita).replace(",","."))||0;
  const p = parseFloat(String(r.prezzoUnitario).replace(",","."))||0;
  const sc = parseFloat(String(r.sconto).replace(",","."))||0;
  const iva = parseFloat(String(r.iva).replace(",","."))||0;
  const imponibile = q*p*(1-sc/100);
  return imponibile*(1+iva/100);
}
export function calcolaImponibileRiga(r) {
  const q = parseFloat(String(r.quantita).replace(",","."))||0;
  const p = parseFloat(String(r.prezzoUnitario).replace(",","."))||0;
  const sc = parseFloat(String(r.sconto).replace(",","."))||0;
  return q*p*(1-sc/100);
}
export function calcolaTotaleODA(righe) {
  return (righe||[]).reduce((s,r)=>s+calcolaTotaleRiga(r),0);
}
// Quanto risulta ricevuto per una riga, sommando tutte le ricezioni registrate.
function calcolaRicevutoRiga(rigaId, ricezioni) {
  return (ricezioni||[]).reduce((s,r)=>{
    const v=(r.righeRicevute||[]).find(x=>x.rigaId===rigaId);
    return s+(v?parseFloat(v.quantitaRicevuta)||0:0);
  },0);
}

export default function ODADetailModal({ oda, orgId, currentUser, canEditAcquisti, canApprovaOda, canChiudiOda, fornitori, commesse, onClose }) {
  const { addToast } = useToast();
  const listino = useListinoMateriali(orgId);
  // La NF da cui è nato l'ordine segue lo stato dell'ODA: il richiedente vede "Ordinata —
  // ODA n. … · Inviato/Ricevuto…" senza dover chiedere all'ufficio Acquisti.
  const aggiornaNfCollegata = useCallback(async (nuovoStato) => {
    if (!oda.nfId) return;
    try { await updateDoc(doc(db,"nuoveForniture",oda.nfId), { odaId: oda.id, odaNumero: oda.numeroODA||oda.numeroOda||"", odaStato: nuovoStato, updatedAt: serverTimestamp() }); }
    catch (e) { console.warn("Stato NF collegata non aggiornato:", e); }
  },[oda.nfId, oda.id, oda.numeroODA, oda.numeroOda]);
  const [righe, setRighe] = useState(()=> oda.righe && oda.righe.length ? oda.righe : [nuovaRiga()]);
  const [fornitoreId, setFornitoreId] = useState(oda.fornitoreId||"");
  const [commessaId, setCommessaId] = useState(oda.commessaId||"");
  const [note, setNote] = useState(oda.note||"");
  const [dataConsegnaPrevista, setDataConsegnaPrevista] = useState(oda.dataConsegnaPrevista||"");
  const [salvando, setSalvando] = useState(false);
  const [inviandoEmail, setInviandoEmail] = useState(false);
  const [tab, setTab] = useState("righe"); // righe | ricezioni | difformita | fattura

  const [formConferma, setFormConferma] = useState({ data:toLocalDateStr(new Date()), dataConsegnaConfermata:oda.dataConsegnaPrevista||"", note:"" });
  const [formRicezione, setFormRicezione] = useState(null); // {data, ddtNumero, note, quantita:{rigaId:val}}
  const [formDifformita, setFormDifformita] = useState(null); // {descrizione}
  const [chiudendoDifformita, setChiudendoDifformita] = useState(null); // {id, esito}
  // Importi già registrati mostrati all'italiana ("1.418,08", non "1418.08")
  const euroIt = (v) => (v===null||v===undefined||v==="") ? "" : (typeof v==="number" ? formatValutaItaliana(v) : v);
  const [formFattura, setFormFattura] = useState({ numero:oda.fattura?.numero||"", data:oda.fattura?.data||"", imponibile:euroIt(oda.fattura?.imponibile), importo:euroIt(oda.fattura?.importo), documentoUrl:oda.fattura?.documentoUrl||"" });

  const stato = oda.stato || "Bozza";
  const bloccata = stato === "Chiuso" || stato === "Annullato";
  const editabileRighe = canEditAcquisti && (stato==="Bozza" || stato==="Da approvare");

  // Ordine nato da una NF (W435): le righe arrivate dalla richiesta restano segnate «da NF»;
  // quelle aggiunte o cambiate dall'ufficio Acquisti sono evidenziate e il richiedente vede
  // nella sua NF che l'ordine è diverso («2 righe aggiunte, 1 modificata»).
  const differenze = useMemo(()=> oda.nfId ? differenzeOdaNf(righe, oda.righeNf || []) : null, [oda.nfId, oda.righeNf, righe]);
  const imponibileTot = useMemo(()=>righe.reduce((s,r)=>s+calcolaImponibileRiga(r),0),[righe]);
  const totaleTot = useMemo(()=>calcolaTotaleODA(righe),[righe]);

  const salvaRighe = useCallback(async ()=>{
    if (righe.every(r=>!r.descrizione.trim())) { addToast("Inserisci almeno una riga con descrizione","error"); return; }
    setSalvando(true);
    try {
      const f = fornitori.find(x=>x.id===fornitoreId);
      const c = commesse.find(x=>x.id===commessaId);
      const d = oda.nfId ? differenzeOdaNf(righe, oda.righeNf || []) : null;
      await updateDoc(doc(db,"ordiniAcquisto",oda.id), {
        righe, note, dataConsegnaPrevista,
        ...(d ? { differenzeNf: { aggiunte: d.aggiunte, modificate: d.modificate, tolte: d.tolte, daNome: currentUser?.displayName || "" } } : {}),
        // Senza fornitore in anagrafica resta il nome scritto (es. quello indicato nella NF):
        // prima il primo salvataggio lo cancellava.
        fornitoreId: fornitoreId||"", fornitoreNome: f?.denominazione || (fornitoreId ? "" : (oda.fornitoreNome||"")),
        commessaId: commessaId||"", commessaNome: c?.name||"",
        importoTotale: totaleTot, imponibileTotale: imponibileTot,
        updatedAt: serverTimestamp(),
      });
      if (d && oda.nfId) {
        try { await updateDoc(doc(db,"nuoveForniture",oda.nfId), { odaDifferenze: { aggiunte: d.aggiunte, modificate: d.modificate, tolte: d.tolte, daNome: currentUser?.displayName || "" }, updatedAt: serverTimestamp() }); }
        catch (e) { console.warn("Differenze non riportate sulla NF:", e); }
      }
      addToast("Salvato ✅","success");
    } catch(err){ addToast(err.message||"Errore nel salvataggio","error"); }
    finally { setSalvando(false); }
  },[righe, note, dataConsegnaPrevista, fornitoreId, commessaId, fornitori, commesse, totaleTot, imponibileTot, oda.id, oda.nfId, oda.righeNf, oda.fornitoreNome, currentUser, addToast]);

  const cambiaStato = useCallback(async (nuovoStato, extra={}) => {
    setSalvando(true);
    try {
      await updateDoc(doc(db,"ordiniAcquisto",oda.id), { stato: nuovoStato, updatedAt: serverTimestamp(), ...extra });
      await aggiornaNfCollegata(nuovoStato);
      await logModifica({
        orgId, uid: currentUser?.uid||"", nomeUtente: currentUser?.displayName||"",
        sezione:"ordiniAcquisto", refId: oda.id, refNome: `ODA #${oda.numeroODA||oda.numeroOda||""}`,
        campo:"stato", campoLabel:"Stato ODA", before: stato, after: nuovoStato,
      });
      addToast(`Stato aggiornato: ${nuovoStato}`,"success");
    } catch(err){ addToast(err.message||"Errore","error"); }
    finally { setSalvando(false); }
  },[oda.id, oda.numeroODA, oda.numeroOda, stato, orgId, currentUser, addToast, aggiornaNfCollegata]);

  const mandaInApprovazione = async () => {
    if (righe.every(r=>!r.descrizione.trim())) { addToast("Aggiungi almeno una riga prima di mandarlo in approvazione","error"); return; }
    await salvaRighe();
    await cambiaStato("Da approvare");
  };

  const approva = async () => {
    if (righe.every(r=>!r.descrizione.trim())) { addToast("Aggiungi almeno una riga prima di approvare","error"); return; }
    if (!await confirm(`Approvare l'ordine per un totale di € ${formatValutaItaliana(totaleTot)}?`)) return;
    await cambiaStato("Approvato", { approvatoDa:currentUser.uid, approvatoDaNome:currentUser.displayName||"", approvatoAt:serverTimestamp() });
  };

  const invia = async () => {
    if (!oda.fornitoreId && !oda.fornitoreNome) { addToast("Serve un fornitore prima di inviare l'ordine","error"); return; }
    if (!await confirm("Inviare l'ordine al fornitore? Da questo momento l'ODA non è più modificabile nelle righe.")) return;
    setInviandoEmail(true);
    try {
      const fn = httpsCallable(getFunctions(), "inviaEmailODA");
      const res = await fn({ odaId: oda.id });
      await cambiaStato("Inviato", { inviatoDa:currentUser.uid, inviatoDaNome:currentUser.displayName||"", inviatoAt:serverTimestamp(), emailInviata: !!res?.data?.ok });
      addToast(res?.data?.ok ? "Ordine inviato via email ✅" : "Stato aggiornato — invio email non riuscito, contatta il fornitore a mano", res?.data?.ok?"success":"info");
    } catch(err) {
      // Anche se l'email fallisce (fornitore senza indirizzo salvato, es.), lo stato
      // avanza comunque — l'invio "di fatto" può essere avvenuto per telefono, e non è
      // giusto bloccare il flusso per una email mancante.
      await cambiaStato("Inviato", { inviatoDa:currentUser.uid, inviatoDaNome:currentUser.displayName||"", inviatoAt:serverTimestamp(), emailInviata:false });
      addToast("Stato aggiornato a Inviato — invio email non riuscito ("+(err.message||"errore")+")","info");
    } finally { setInviandoEmail(false); }
  };

  const registraConferma = async () => {
    if (!await confirm("Registrare la conferma del fornitore?")) return;
    await cambiaStato("Confermato", { conferma: { ...formConferma, registrataDa:currentUser.uid, registrataDaNome:currentUser.displayName||"" } });
  };

  const salvaRicezione = async () => {
    if (!formRicezione) return;
    const righeRicevute = Object.entries(formRicezione.quantita||{}).filter(([,v])=>v && parseFloat(v)>0).map(([rigaId,v])=>({rigaId, quantitaRicevuta:parseFloat(v)}));
    if (righeRicevute.length===0) { addToast("Indica almeno una quantità ricevuta","error"); return; }
    // Nessuna riga può ricevere più di quanto ne resta da ricevere — un errore di battitura
    // qui altrimenti gonfierebbe silenziosamente il ricevuto oltre l'ordinato.
    for (const rr of righeRicevute) {
      const riga = righe.find(r=>r.id===rr.rigaId);
      const q = parseFloat(String(riga?.quantita).replace(",","."))||0;
      const giaRicevuto = calcolaRicevutoRiga(rr.rigaId, oda.ricezioni);
      const residuo = Math.max(0, q - giaRicevuto);
      if (rr.quantitaRicevuta > residuo + 0.0001) {
        addToast(`"${riga?.descrizione}": stai registrando ${rr.quantitaRicevuta}, ma il residuo è solo ${residuo}`,"error");
        return;
      }
    }
    const nuoveRicezioni = [...(oda.ricezioni||[]), { id:`ric_${Date.now()}`, data:formRicezione.data, ddtNumero:formRicezione.ddtNumero||"", note:formRicezione.note||"", righeRicevute, registrataDa:currentUser.uid, registrataDaNome:currentUser.displayName||"" }];
    // Calcola se tutte le righe risultano ora completamente ricevute
    const tutteComplete = righe.every(r=>{
      const q = parseFloat(String(r.quantita).replace(",","."))||0;
      if (q<=0) return true;
      return calcolaRicevutoRiga(r.id, nuoveRicezioni) >= q - 0.0001;
    });
    const nuovoStato = tutteComplete ? "Ricevuto" : "Parzialmente ricevuto";
    setSalvando(true);
    try {
      await updateDoc(doc(db,"ordiniAcquisto",oda.id), { ricezioni: nuoveRicezioni, stato: nuovoStato, updatedAt: serverTimestamp() });
      await aggiornaNfCollegata(nuovoStato);
      await logModifica({ orgId, uid:currentUser?.uid||"", nomeUtente:currentUser?.displayName||"", sezione:"ordiniAcquisto", refId:oda.id, refNome:`ODA #${oda.numeroODA||oda.numeroOda||""}`, campo:"ricezione", campoLabel:"Ricezione registrata", before:stato, after:nuovoStato });
      setFormRicezione(null);
      addToast(`Ricezione registrata — ${nuovoStato}`,"success");
    } catch(err){ addToast(err.message||"Errore","error"); }
    finally { setSalvando(false); }
  };

  const salvaDifformita = async () => {
    if (!formDifformita?.descrizione?.trim()) { addToast("Descrivi la difformità","error"); return; }
    const nuove = [...(oda.difformita||[]), { id:`dif_${Date.now()}`, data:toLocalDateStr(new Date()), descrizione:formDifformita.descrizione.trim(), esito:"", chiusa:false, segnalataDa:currentUser.uid, segnalataDaNome:currentUser.displayName||"" }];
    setSalvando(true);
    try {
      await updateDoc(doc(db,"ordiniAcquisto",oda.id), { difformita: nuove, updatedAt: serverTimestamp() });
      await logModifica({ orgId, uid:currentUser?.uid||"", nomeUtente:currentUser?.displayName||"", sezione:"ordiniAcquisto", refId:oda.id, refNome:`ODA #${oda.numeroODA||oda.numeroOda||""}`, campo:"difformita", campoLabel:"Difformità segnalata", before:"—", after: formDifformita.descrizione.trim() });
      setFormDifformita(null);
      addToast("Difformità registrata","success");
    } catch(err){ addToast(err.message||"Errore","error"); }
    finally { setSalvando(false); }
  };

  const chiudiDifformita = async (id, esito) => {
    const nuove = (oda.difformita||[]).map(d=>d.id===id?{...d,esito:esito||d.esito,chiusa:true}:d);
    await updateDoc(doc(db,"ordiniAcquisto",oda.id), { difformita: nuove, updatedAt: serverTimestamp() });
    await logModifica({ orgId, uid:currentUser?.uid||"", nomeUtente:currentUser?.displayName||"", sezione:"ordiniAcquisto", refId:oda.id, refNome:`ODA #${oda.numeroODA||oda.numeroOda||""}`, campo:"difformita", campoLabel:"Difformità chiusa", before:"Aperta", after:esito||"Chiusa" });
  };

  const salvaFattura = async () => {
    // Controllo a tre vie (ordinato / ricevuto / fatturato) sull'IMPONIBILE: è l'importo che
    // diventa costo di commessa (IVA esclusa). Il totale con IVA resta per riscontro.
    if (!String(formFattura.numero).trim() || formFattura.imponibile==="" || !formFattura.importo) { addToast("Numero, imponibile e totale della fattura sono obbligatori","error"); return; }
    const imponibileF = parseValutaItaliana(formFattura.imponibile), totaleF = parseValutaItaliana(formFattura.importo);
    if (imponibileF > totaleF + 0.01) { addToast("L'imponibile non può superare il totale della fattura","error"); return; }
    setSalvando(true);
    try {
      const differenza = imponibileF - imponibileTot;
      await updateDoc(doc(db,"ordiniAcquisto",oda.id), {
        fattura: { ...formFattura, imponibile: imponibileF, iva: Math.round((totaleF-imponibileF)*100)/100, importo: totaleF, registrataDa:currentUser.uid, registrataDaNome:currentUser.displayName||"", registrataAt: serverTimestamp() },
        stato: stato==="Ricevuto"||stato==="Parzialmente ricevuto" ? "Fattura verificata" : stato,
        updatedAt: serverTimestamp(),
      });
      if (stato==="Ricevuto"||stato==="Parzialmente ricevuto") await aggiornaNfCollegata("Fattura verificata");
      await logModifica({ orgId, uid:currentUser?.uid||"", nomeUtente:currentUser?.displayName||"", sezione:"ordiniAcquisto", refId:oda.id, refNome:`ODA #${oda.numeroODA||oda.numeroOda||""}`, campo:"fattura", campoLabel:"Fattura collegata", before:"—", after:`${formFattura.numero} · € ${formatValutaItaliana(parseValutaItaliana(formFattura.importo))}` });
      if (Math.abs(differenza) > 0.01) addToast(`Attenzione: l'imponibile della fattura differisce da quello dell'ordine di € ${formatValutaItaliana(Math.abs(differenza))}`,"info");
      else addToast("Fattura registrata ✅","success");
    } catch(err){ addToast(err.message||"Errore","error"); }
    finally { setSalvando(false); }
  };

  const chiudiOda = async () => {
    if (!await confirm("Chiudere definitivamente questo ordine? Da questo momento non sarà più modificabile.", { type:"danger" })) return;
    await cambiaStato("Chiuso", { chiusoDa:currentUser.uid, chiusoDaNome:currentUser.displayName||"", chiusoAt:serverTimestamp() });
  };

  const annulla = async () => {
    if (!await confirm("Annullare questo ordine di acquisto?", { type:"danger" })) return;
    await cambiaStato("Annullato", { annullatoDa:currentUser.uid, annullatoDaNome:currentUser.displayName||"", annullatoAt:serverTimestamp() });
  };

  const stampaPDF = () => {
    const win = window.open("","_blank");
    const fornitore = fornitori.find(f=>f.id===oda.fornitoreId);
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>ODA ${oda.numeroODA||""}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:14mm 16mm;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4;}
      .hdr{border-bottom:3px solid #1e1b4b;padding-bottom:10px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:flex-start;}
      .info{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:12px 16px;margin-bottom:16px;}
      .lbl{font-size:9px;color:#9ca3af;font-weight:700;text-transform:uppercase;display:block;margin-bottom:2px;}
      .val{font-size:12px;font-weight:600;}
      table{width:100%;border-collapse:collapse;margin-bottom:16px;}
      th{background:#1e1b4b;color:#fff;font-size:10px;text-align:left;padding:6px 8px;}
      td{padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:11px;}
      .tot{text-align:right;font-size:14px;font-weight:800;color:#1e1b4b;}
      .footer{border-top:1px solid #e5e7eb;margin-top:16px;padding-top:8px;font-size:9px;color:#9ca3af;display:flex;justify-content:space-between;}
    </style></head><body>
    <div class="hdr">
      <div>
        <div style="font-size:16px;font-weight:800;color:#1e1b4b">ORDINE DI ACQUISTO</div>
        <div style="font-size:18px;font-weight:900;color:#7c3aed;font-family:monospace">${oda.numeroODA||""}</div>
      </div>
      <span style="padding:4px 12px;border-radius:20px;font-size:11px;font-weight:700;background:#eff6ff;color:#2563eb">${stato}</span>
    </div>
    <div class="info">
      <div><span class="lbl">Fornitore</span><span class="val">${fornitore?.denominazione||oda.fornitoreNome||"—"}</span></div>
      <div><span class="lbl">P.IVA fornitore</span><span class="val">${fornitore?.piva||"—"}</span></div>
      <div><span class="lbl">Commessa</span><span class="val">${oda.commessaNome||oda.commessaLibera||"—"}</span></div>
      <div><span class="lbl">Data ordine</span><span class="val">${oda.data?new Date(oda.data+"T00:00:00").toLocaleDateString("it-IT"):"—"}</span></div>
      <div><span class="lbl">Consegna prevista</span><span class="val">${dataConsegnaPrevista?new Date(dataConsegnaPrevista+"T00:00:00").toLocaleDateString("it-IT"):"—"}</span></div>
      <div><span class="lbl">Note</span><span class="val" style="font-weight:400">${note||"—"}</span></div>
    </div>
    <table>
      <thead><tr><th>Descrizione</th><th>Cod.</th><th>Q.tà</th><th>U.M.</th><th>Prezzo unit.</th><th>Sconto</th><th>IVA</th><th style="text-align:right">Totale</th></tr></thead>
      <tbody>
        ${righe.filter(r=>r.descrizione.trim()).map(r=>`<tr><td>${r.descrizione}</td><td>${r.codice||"—"}</td><td>${r.quantita||"—"}</td><td>${r.unitaMisura||"—"}</td><td>€ ${formatValutaItaliana(parseFloat(r.prezzoUnitario)||0)}</td><td>${r.sconto||0}%</td><td>${r.iva||22}%</td><td style="text-align:right;font-weight:700">€ ${formatValutaItaliana(calcolaTotaleRiga(r))}</td></tr>`).join("")}
      </tbody>
    </table>
    <div class="tot">Totale ordine: € ${formatValutaItaliana(totaleTot)}</div>
    <div class="footer">
      <span>Generato da Nexiora — ${dominioSito()}</span>
      <span>Stampato il ${new Date().toLocaleDateString("it-IT")}</span>
    </div>
    <script>window.onload=()=>window.print();</script>
    </body></html>`);
    win.document.close();
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div role="dialog" aria-label={`Ordine di acquisto ${oda.numeroODA||oda.numeroOda||""}`} style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:920,maxHeight:"92vh",display:"flex",flexDirection:"column",overflow:"hidden"}} onClick={e=>e.stopPropagation()}>

        {/* HEADER */}
        <div style={{padding:"16px 22px",borderBottom:"1px solid #e5e7eb",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div>
            <div style={{fontSize:11,color:"#9ca3af",fontWeight:700}}>ORDINE DI ACQUISTO</div>
            <div style={{fontSize:18,fontWeight:800,color:"#7c3aed",fontFamily:"monospace"}}>#{oda.numeroODA||oda.numeroOda}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:11,fontWeight:700,padding:"4px 12px",borderRadius:20,background:STATO_ODA_COLORI[stato]?.bg,color:STATO_ODA_COLORI[stato]?.text}}>{stato}</span>
            <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:"#6b7280"}}><X size={20}/></button>
          </div>
        </div>

        {/* AZIONI DI STATO */}
        <div style={{padding:"10px 22px",background:"#faf9ff",borderBottom:"1px solid #e5e7eb",display:"flex",gap:8,flexWrap:"wrap",flexShrink:0}}>
          <button onClick={stampaPDF} style={btnGhost}><Printer size={13}/> PDF</button>
          {editabileRighe && <button onClick={salvaRighe} disabled={salvando} style={btnGhost}>💾 Salva righe</button>}
          {stato==="Bozza" && canEditAcquisti && !canApprovaOda && <button onClick={mandaInApprovazione} disabled={salvando} style={btnPrimary}><Send size={13}/> Manda in approvazione</button>}
          {(stato==="Bozza"||stato==="Da approvare") && canApprovaOda && <button onClick={approva} disabled={salvando} style={btnPrimary}><CheckCircle2 size={13}/> Approva</button>}
          {stato==="Approvato" && canEditAcquisti && <button onClick={invia} disabled={salvando||inviandoEmail} style={btnPrimary}><Send size={13}/> {inviandoEmail?"Invio...":"Invia al fornitore"}</button>}
          {(stato==="Inviato") && canEditAcquisti && <button onClick={()=>setTab("conferma")} style={btnGhost}>📩 Registra conferma</button>}
          {(stato==="Confermato"||stato==="Parzialmente ricevuto") && canEditAcquisti && <button onClick={()=>setTab("ricezioni")} style={btnPrimary}><PackageCheck size={13}/> Registra ricezione</button>}
          {(stato==="Ricevuto"||stato==="Parzialmente ricevuto") && canEditAcquisti && <button onClick={()=>setTab("fattura")} style={btnGhost}><Receipt size={13}/> Collega fattura</button>}
          {(stato==="Ricevuto"||stato==="Fattura verificata") && canChiudiOda && <button onClick={chiudiOda} disabled={salvando} style={btnPrimary}><Lock size={13}/> Chiudi ordine</button>}
          {!bloccata && canApprovaOda && <button onClick={annulla} style={{...btnGhost,color:"#dc2626",borderColor:"#fca5a5"}}>Annulla ordine</button>}
        </div>

        {/* TABS INTERNE */}
        <div style={{display:"flex",gap:4,padding:"8px 22px 0",borderBottom:"1px solid #e5e7eb",flexShrink:0}}>
          {[
            {id:"righe",label:"Righe"},
            {id:"conferma",label:"Conferma fornitore"},
            {id:"ricezioni",label:`Ricezioni${oda.ricezioni?.length?` (${oda.ricezioni.length})`:""}`},
            {id:"difformita",label:`Difformità${oda.difformita?.length?` (${oda.difformita.length})`:""}`},
            {id:"fattura",label:"Fattura"},
          ].map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{padding:"7px 14px",border:"none",borderBottom:tab===t.id?"2px solid #7c3aed":"2px solid transparent",
                background:"none",color:tab===t.id?"#7c3aed":"#6b7280",fontWeight:tab===t.id?700:400,cursor:"pointer",fontSize:12.5,fontFamily:"'Inter',sans-serif"}}>
              {t.label}
            </button>
          ))}
        </div>

        {/* CONTENUTO */}
        <div style={{flex:1,overflowY:"auto",padding:20}}>

          {tab==="righe" && (
            <>
              {oda.nfId && (
                <div style={{background:"#ecfdf5",border:"1px solid #a7f3d0",borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:12,color:"#065f46",whiteSpace:"pre-line"}}>
                  📋 Nato dalla <strong>NF #{oda.nfNumero||"—"}</strong>{oda.nfDescrizione?` — ${oda.nfDescrizione}`:""}{oda.nfCompilatore?` · richiesta da ${oda.nfCompilatore}`:""}
                  {oda.rdoNumero && <div style={{marginTop:4}}>📨 Dall'offerta scelta: richiesta <strong>{oda.rdoNumero}</strong> a {oda.fornitoreNome||"—"}{oda.offertaTotale?` · offerta € ${formatValutaItaliana(oda.offertaTotale)} IVA esclusa`:""}</div>}
                </div>
              )}
              <datalist id="listino-oda">
                {listino.slice(0,500).map(v=><option key={v.id} value={v.descrizione}>{[v.codiceArticolo,v.unitaMisura,v.prezzoUnitario!=null&&v.prezzoUnitario!==""?`€ ${v.prezzoUnitario}`:""].filter(Boolean).join(" · ")}</option>)}
              </datalist>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Fornitore</label>
                  <select className="input" disabled={!editabileRighe} value={fornitoreId} onChange={e=>setFornitoreId(e.target.value)} style={{width:"100%"}}>
                    <option value="">— Nessuno —</option>
                    {fornitori.map(f=><option key={f.id} value={f.id}>{f.denominazione}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Commessa</label>
                  <select className="input" disabled={!editabileRighe} value={commessaId} onChange={e=>setCommessaId(e.target.value)} style={{width:"100%"}}>
                    <option value="">— Nessuna —</option>
                    {commesse.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Consegna prevista</label>
                  <input type="date" className="input" value={dataConsegnaPrevista} disabled={!editabileRighe} onChange={e=>setDataConsegnaPrevista(e.target.value)} style={{width:"100%"}}/>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Note</label>
                  <input className="input" value={note} disabled={!editabileRighe} onChange={e=>setNote(e.target.value)} style={{width:"100%"}}/>
                </div>
              </div>

              {/* W408: lo stesso materiale è già chiesto o ordinato per questa commessa? */}
              {["Bozza","Da approvare","Approvato"].includes(stato) && (
                <PannelloDoppioni orgId={orgId} projectId={commessaId} righe={righe} escludiDoc={`oda:${oda.id}`} nonDoppioni={oda.nonDoppioni||[]}
                  puoSegnare={canEditAcquisti}
                  onNonDoppione={chiave => updateDoc(doc(db,"ordiniAcquisto",oda.id), { nonDoppioni: arrayUnion(chiave), updatedAt: serverTimestamp() })}/>
              )}
              <div style={{overflowX:"auto",border:"1px solid #e5e7eb",borderRadius:8}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:11.5,minWidth:760}}>
                  <thead><tr style={{background:"#f9fafb"}}>
                    {[...(oda.nfId?["Origine"]:[]),"Descrizione","Codice","Q.tà","U.M.","Prezzo unit.","Sconto %","IVA %","Totale",""].map(h=>(
                      <th key={h} style={{padding:"6px 8px",textAlign:"left",fontSize:10,fontWeight:700,color:"#6b7280"}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {righe.map((r,i)=>(
                      <tr key={r.id} style={{borderTop:"1px solid #f3f4f6",background:differenze?.perRiga?.[r.id]==="aggiunta"?"#fffbeb":differenze?.perRiga?.[r.id]==="modificata"?"#fff7ed":undefined}}>
                        {oda.nfId && <td style={{padding:4,whiteSpace:"nowrap"}}>{(()=>{ const t=differenze?.perRiga?.[r.id];
                          if (t==="aggiunta") return <span title="Riga aggiunta dall'ufficio Acquisti: non c'era nella NF" style={{fontSize:10,fontWeight:700,color:"#b45309",background:"#fef3c7",borderRadius:10,padding:"2px 7px"}}>+ aggiunta</span>;
                          if (t==="modificata") return <span title={`Nella NF: ${r.daNf?.quantita||""} ${r.daNf?.unitaMisura||""} ${r.daNf?.descrizione||""}`.replace(/\s+/g," ")} style={{fontSize:10,fontWeight:700,color:"#c2410c",background:"#ffedd5",borderRadius:10,padding:"2px 7px"}}>✎ modificata</span>;
                          return <span title="Come nella NF" style={{fontSize:10,fontWeight:700,color:"#6d28d9",background:"#ede9fe",borderRadius:10,padding:"2px 7px"}}>da NF</span>; })()}</td>}
                        <td style={{padding:4}}><input className="input" list="listino-oda" disabled={!editabileRighe} value={r.descrizione} onChange={e=>setRighe(rs=>rs.map((x,j)=>{ if(j!==i) return x; const v=trovaInListino(listino,e.target.value); return v?{...x,descrizione:v.descrizione,codice:x.codice||v.codiceArticolo||"",unitaMisura:x.unitaMisura||v.unitaMisura||"",prezzoUnitario:x.prezzoUnitario||(v.prezzoUnitario!=null?String(v.prezzoUnitario):""),sconto:x.sconto||(v.scontoAbituale!=null?String(v.scontoAbituale):"")}:{...x,descrizione:e.target.value}; }))} style={{fontSize:11,padding:"3px 5px",width:"100%"}}/></td>
                        <td style={{padding:4}}><input className="input" disabled={!editabileRighe} value={r.codice} onChange={e=>setRighe(rs=>rs.map((x,j)=>j===i?{...x,codice:e.target.value}:x))} style={{fontSize:11,padding:"3px 5px",width:70}}/></td>
                        <td style={{padding:4}}><input className="input" disabled={!editabileRighe} value={r.quantita} onChange={e=>setRighe(rs=>rs.map((x,j)=>j===i?{...x,quantita:e.target.value}:x))} style={{fontSize:11,padding:"3px 5px",width:55}}/></td>
                        <td style={{padding:4}}><input className="input" disabled={!editabileRighe} value={r.unitaMisura} onChange={e=>setRighe(rs=>rs.map((x,j)=>j===i?{...x,unitaMisura:e.target.value}:x))} style={{fontSize:11,padding:"3px 5px",width:55}}/></td>
                        <td style={{padding:4}}><input className="input" disabled={!editabileRighe} value={r.prezzoUnitario} onChange={e=>setRighe(rs=>rs.map((x,j)=>j===i?{...x,prezzoUnitario:e.target.value}:x))} style={{fontSize:11,padding:"3px 5px",width:75}}/></td>
                        <td style={{padding:4}}><input className="input" disabled={!editabileRighe} value={r.sconto} onChange={e=>setRighe(rs=>rs.map((x,j)=>j===i?{...x,sconto:e.target.value}:x))} style={{fontSize:11,padding:"3px 5px",width:55}}/></td>
                        <td style={{padding:4}}><input className="input" disabled={!editabileRighe} value={r.iva} onChange={e=>setRighe(rs=>rs.map((x,j)=>j===i?{...x,iva:e.target.value}:x))} style={{fontSize:11,padding:"3px 5px",width:50}}/></td>
                        <td style={{padding:4,fontWeight:700,color:"#059669",whiteSpace:"nowrap"}}>€ {formatValutaItaliana(calcolaTotaleRiga(r))}</td>
                        <td style={{padding:4}}>{editabileRighe && righe.length>1 && <button onClick={()=>setRighe(rs=>rs.filter((_,j)=>j!==i))} style={{background:"none",border:"none",cursor:"pointer",color:"#d1d5db"}}><Trash2 size={13}/></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {editabileRighe && <button onClick={()=>setRighe(rs=>[...rs,nuovaRiga()])} style={{...btnGhost,marginTop:8}}><Plus size={13}/> Aggiungi riga</button>}
              {differenze?.diversa && (
                <div style={{marginTop:8,fontSize:12,color:"#b45309",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"6px 10px"}}>
                  ⚠️ L'ordine è diverso dalla NF #{oda.nfNumero||""}: {testoDifferenze(differenze)}. Salvando, chi ha fatto la richiesta lo vede nella sua NF.
                </div>
              )}

              <div style={{marginTop:16,display:"flex",justifyContent:"flex-end",gap:24,fontSize:13}}>
                <div>Imponibile: <strong>€ {formatValutaItaliana(imponibileTot)}</strong></div>
                <div style={{fontSize:16,fontWeight:800,color:"#1e1b4b"}}>Totale: € {formatValutaItaliana(totaleTot)}</div>
              </div>
            </>
          )}

          {tab==="conferma" && (
            <div>
              {oda.conferma ? (
                <div style={{background:"#f0fdfa",border:"1px solid #99f6e4",borderRadius:8,padding:14,fontSize:12.5}}>
                  <div><strong>Confermata il:</strong> {oda.conferma.data}</div>
                  <div><strong>Consegna confermata:</strong> {oda.conferma.dataConsegnaConfermata||"—"}</div>
                  <div><strong>Note:</strong> {oda.conferma.note||"—"}</div>
                </div>
              ) : (
                <div style={{maxWidth:400,display:"flex",flexDirection:"column",gap:10}}>
                  <div>
                    <label style={lblSm}>Data conferma</label>
                    <input type="date" className="input" value={formConferma.data} onChange={e=>setFormConferma(f=>({...f,data:e.target.value}))} style={{width:"100%"}}/>
                  </div>
                  <div>
                    <label style={lblSm}>Data consegna confermata</label>
                    <input type="date" className="input" value={formConferma.dataConsegnaConfermata} onChange={e=>setFormConferma(f=>({...f,dataConsegnaConfermata:e.target.value}))} style={{width:"100%"}}/>
                  </div>
                  <div>
                    <label style={lblSm}>Note / variazioni</label>
                    <textarea className="input" rows={3} value={formConferma.note} onChange={e=>setFormConferma(f=>({...f,note:e.target.value}))} style={{width:"100%"}}/>
                  </div>
                  {canEditAcquisti && stato==="Inviato" && <button onClick={registraConferma} style={btnPrimary}>Registra conferma</button>}
                </div>
              )}
            </div>
          )}

          {tab==="ricezioni" && (
            <div>
              {(oda.ricezioni||[]).map(r=>(
                <div key={r.id} style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:8,padding:12,marginBottom:8,fontSize:12}}>
                  <div style={{fontWeight:700,marginBottom:4}}>{new Date(r.data+"T00:00:00").toLocaleDateString("it-IT")} {r.ddtNumero?`· DDT ${r.ddtNumero}`:""}</div>
                  {r.righeRicevute.map(rr=>{
                    const rgOrig = righe.find(x=>x.id===rr.rigaId);
                    return <div key={rr.rigaId} style={{color:"#6b7280"}}>{rgOrig?.descrizione||"—"}: <strong>{rr.quantitaRicevuta}</strong> {rgOrig?.unitaMisura||""}</div>;
                  })}
                  {r.note && <div style={{marginTop:4,fontStyle:"italic",color:"#9ca3af"}}>{r.note}</div>}
                </div>
              ))}
              {(!oda.ricezioni||oda.ricezioni.length===0) && <div style={{color:"#9ca3af",fontSize:12,marginBottom:12}}>Nessuna ricezione ancora registrata.</div>}

              {canEditAcquisti && !bloccata && (stato==="Confermato"||stato==="Parzialmente ricevuto"||stato==="Inviato") && (
                formRicezione ? (
                  <div style={{border:"1px solid #7c3aed",borderRadius:8,padding:14,marginTop:8}}>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                      <div><label style={lblSm}>Data ricezione</label><input type="date" className="input" value={formRicezione.data} onChange={e=>setFormRicezione(f=>({...f,data:e.target.value}))} style={{width:"100%"}}/></div>
                      <div><label style={lblSm}>N° DDT (facoltativo)</label><input className="input" value={formRicezione.ddtNumero} onChange={e=>setFormRicezione(f=>({...f,ddtNumero:e.target.value}))} style={{width:"100%"}}/></div>
                    </div>
                    <div style={{fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:6}}>Quantità ricevute per riga</div>
                    {righe.filter(r=>r.descrizione.trim()).map(r=>{
                      const q=parseFloat(String(r.quantita).replace(",","."))||0;
                      const giaRicevuto = calcolaRicevutoRiga(r.id, oda.ricezioni);
                      const residuo = Math.max(0,q-giaRicevuto);
                      return (
                        <div key={r.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                          <div style={{flex:1,fontSize:12}}>{r.descrizione} <span style={{color:"#9ca3af"}}>(ordinati {q||"—"}, già ricevuti {giaRicevuto})</span></div>
                          <input className="input" placeholder={`max ${residuo}`} value={formRicezione.quantita?.[r.id]||""} onChange={e=>setFormRicezione(f=>({...f,quantita:{...f.quantita,[r.id]:e.target.value}}))} style={{width:80,fontSize:11}}/>
                        </div>
                      );
                    })}
                    <div><label style={lblSm}>Note</label><input className="input" value={formRicezione.note} onChange={e=>setFormRicezione(f=>({...f,note:e.target.value}))} style={{width:"100%"}}/></div>
                    <div style={{display:"flex",gap:8,marginTop:10}}>
                      <button onClick={salvaRicezione} disabled={salvando} style={btnPrimary}>Registra ricezione</button>
                      <button onClick={()=>setFormRicezione(null)} style={btnGhost}>Annulla</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={()=>setFormRicezione({ data:toLocalDateStr(new Date()), ddtNumero:"", note:"", quantita:{} })} style={btnPrimary}><Plus size={13}/> Nuova ricezione</button>
                )
              )}
            </div>
          )}

          {tab==="difformita" && (
            <div>
              {(oda.difformita||[]).map(d=>(
                <div key={d.id} style={{background:d.chiusa?"#f9fafb":"#fffbeb",border:`1px solid ${d.chiusa?"#e5e7eb":"#fde68a"}`,borderRadius:8,padding:12,marginBottom:8,fontSize:12}}>
                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <div style={{fontWeight:700}}>{new Date(d.data+"T00:00:00").toLocaleDateString("it-IT")}</div>
                    <span style={{fontSize:10,fontWeight:700,color:d.chiusa?"#059669":"#b45309"}}>{d.chiusa?"Chiusa":"Aperta"}</span>
                  </div>
                  <div style={{margin:"4px 0"}}>{d.descrizione}</div>
                  {d.esito && <div style={{color:"#6b7280"}}><strong>Esito:</strong> {d.esito}</div>}
                  {!d.chiusa && canEditAcquisti && (
                    chiudendoDifformita?.id===d.id ? (
                      <div style={{marginTop:8,display:"flex",gap:6}}>
                        <input className="input" autoFocus placeholder="Esito (es. sostituzione, nota di credito, accettata così)"
                          value={chiudendoDifformita.esito} onChange={e=>setChiudendoDifformita({id:d.id,esito:e.target.value})}
                          style={{flex:1,fontSize:12}}/>
                        <button onClick={()=>{ chiudiDifformita(d.id, chiudendoDifformita.esito); setChiudendoDifformita(null); }} style={{...btnGhost,padding:"4px 10px"}}>Conferma</button>
                        <button onClick={()=>setChiudendoDifformita(null)} style={{...btnGhost,padding:"4px 10px"}}>Annulla</button>
                      </div>
                    ) : (
                      <button onClick={()=>setChiudendoDifformita({id:d.id,esito:""})} style={{...btnGhost,marginTop:6,padding:"4px 10px"}}>Chiudi difformità</button>
                    )
                  )}
                </div>
              ))}
              {(!oda.difformita||oda.difformita.length===0) && <div style={{color:"#9ca3af",fontSize:12,marginBottom:12}}>Nessuna difformità registrata.</div>}

              {canEditAcquisti && !bloccata && (
                formDifformita ? (
                  <div style={{border:"1px solid #dc2626",borderRadius:8,padding:14}}>
                    <label style={lblSm}>Descrizione difformità</label>
                    <textarea className="input" rows={3} value={formDifformita.descrizione} onChange={e=>setFormDifformita({descrizione:e.target.value})} style={{width:"100%",marginBottom:8}}/>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={salvaDifformita} style={{...btnPrimary,background:"#dc2626"}}><FileWarning size={13}/> Registra</button>
                      <button onClick={()=>setFormDifformita(null)} style={btnGhost}>Annulla</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={()=>setFormDifformita({descrizione:""})} style={{...btnGhost,color:"#dc2626",borderColor:"#fca5a5"}}><Plus size={13}/> Segnala difformità</button>
                )
              )}
            </div>
          )}

          {tab==="fattura" && (
            <div style={{maxWidth:440}}>
              {oda.fattura?.registrataAt && (
                <div style={{background:"#f5f3ff",border:"1px solid #ddd6fe",borderRadius:8,padding:12,marginBottom:14,fontSize:12}}>
                  Fattura già registrata da <strong>{oda.fattura.registrataDaNome}</strong>
                  {oda.fattura.imponibile!=null && oda.fattura.imponibile!==""
                    ? (Math.abs((oda.fattura.imponibile||0)-imponibileTot)>0.01 && <div style={{color:"#dc2626",fontWeight:700,marginTop:4}}>⚠️ Imponibile diverso dall'ordine: € {formatValutaItaliana(Math.abs(oda.fattura.imponibile-imponibileTot))}</div>)
                    : (<>
                        {Math.abs((oda.fattura.importo||0)-totaleTot)>0.01 && <div style={{color:"#dc2626",fontWeight:700,marginTop:4}}>⚠️ Differenza rispetto all'ordine: € {formatValutaItaliana(Math.abs(oda.fattura.importo-totaleTot))}</div>}
                        <div style={{color:"#92400e",marginTop:4}}>Fattura registrata con il solo totale: inserisci l'imponibile e salva di nuovo, così il costo in commessa è quello esatto.</div>
                      </>)}
                </div>
              )}
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <div><label style={lblSm}>Numero fattura *</label><input className="input" value={formFattura.numero} onChange={e=>setFormFattura(f=>({...f,numero:e.target.value}))} style={{width:"100%"}}/></div>
                <div><label style={lblSm}>Data fattura</label><input type="date" className="input" value={formFattura.data} onChange={e=>setFormFattura(f=>({...f,data:e.target.value}))} style={{width:"100%"}}/></div>
                <div><label style={lblSm}>Imponibile fattura, IVA esclusa (€) *</label><input className="input" value={formFattura.imponibile} onChange={e=>setFormFattura(f=>({...f,imponibile:e.target.value}))} placeholder={`Ordine: € ${formatValutaItaliana(imponibileTot)}`} style={{width:"100%"}}/>
                  <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>È questo l'importo che diventa costo della commessa.</div></div>
                <div><label style={lblSm}>Totale fattura, IVA inclusa (€) *</label><input className="input" value={formFattura.importo} onChange={e=>setFormFattura(f=>({...f,importo:e.target.value}))} placeholder={`Ordine: € ${formatValutaItaliana(totaleTot)}`} style={{width:"100%"}}/></div>
                <div><label style={lblSm}>Link/riferimento documento</label><input className="input" value={formFattura.documentoUrl} onChange={e=>setFormFattura(f=>({...f,documentoUrl:e.target.value}))} style={{width:"100%"}}/></div>
                {canEditAcquisti && !bloccata && <button onClick={salvaFattura} disabled={salvando} style={btnPrimary}><Receipt size={13}/> Salva fattura</button>}
                {bloccata && <div style={{fontSize:11,color:"#9ca3af"}}>Ordine {stato.toLowerCase()} — la fattura non è più modificabile.</div>}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

const btnPrimary = { display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:"#7c3aed",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif" };
const btnGhost = { display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:"#fff",color:"#374151",border:"1px solid #e5e7eb",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif" };
const lblSm = { fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4 };
